/* ==========================================================================
   XELPHORA — product.js
   Product page behaviour. Loaded with `defer` only on product templates, and
   always after global.js, so window.Xelphora.cart and .utils already exist.

   Division of labour with the server:
   - Anything involving money, tax, discounts or stock is re-rendered by
     Shopify via the Section Rendering API. This file never formats a price.
   - Anything purely presentational — which media is on screen, which swatch
     looks selected, which combinations are reachable — is resolved instantly
     from a compact variant map, with no network round trip.
   ========================================================================== */

(() => {
  'use strict';

  const Theme = (window.Xelphora = window.Xelphora || {});
  const routes = Theme.routes || {};
  const strings = Theme.strings || {};
  const config = Theme.settings || {};

  const reduceMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const scrollBehavior = () => (reduceMotion() ? 'auto' : 'smooth');

  /* ========================================================================
     1. Media gallery
     ======================================================================== */
  class ProductGallery extends HTMLElement {
    connectedCallback() {
      this.stage = this.querySelector('[data-gallery-stage]');
      if (!this.stage) return;

      this.slides = Array.from(this.querySelectorAll('[data-gallery-slide]'));
      this.thumbs = Array.from(this.querySelectorAll('[data-gallery-thumb]'));
      this.thumbRail = this.querySelector('[data-gallery-thumbs]');
      this.prev = this.querySelector('[data-gallery-prev]');
      this.next = this.querySelector('[data-gallery-next]');
      this.counter = this.querySelector('[data-gallery-current]');
      // Open on the selected variant's image when it has one.
      const initial = this.slides.findIndex((s) => s.dataset.mediaId === this.dataset.initialMedia);
      this.index = initial >= 0 ? initial : 0;

      this.bindThumbs();
      this.bindArrows();
      this.bindStageTracking();
      this.bindExternalVideo();

      this.syncArrows();
    }

    disconnectedCallback() {
      this.slideObserver?.disconnect();
      this.resizeObserver?.disconnect();
    }

    /** True while the stage is actually a horizontal scroller. In the stacked
     *  desktop layout it is a plain column, and navigation becomes a scroll
     *  into view instead of a scroll-left. */
    get isScroller() {
      return this.stage.scrollWidth > this.stage.clientWidth + 1;
    }

    bindThumbs() {
      this.thumbs.forEach((thumb) => {
        thumb.addEventListener('click', () => this.goTo(Number(thumb.dataset.index)));
      });
    }

    bindArrows() {
      this.prev?.addEventListener('click', () => this.goTo(this.index - 1));
      this.next?.addEventListener('click', () => this.goTo(this.index + 1));

      if ('ResizeObserver' in window) {
        this.resizeObserver = new ResizeObserver(() => this.syncArrows());
        this.resizeObserver.observe(this.stage);
      }
    }

    /** Observing the slides against the stage is more reliable than dividing
     *  scrollLeft by a slide width, which drifts with fractional widths and
     *  with mixed media sizes. */
    bindStageTracking() {
      if (!('IntersectionObserver' in window) || this.slides.length < 2) return;

      this.slideObserver = new IntersectionObserver(
        (entries) => {
          const visible = entries
            .filter((e) => e.isIntersecting)
            .sort((a, b) => b.intersectionRatio - a.intersectionRatio)[0];
          if (!visible) return;
          this.setActive(Number(visible.target.dataset.index), { scroll: false });
        },
        { root: this.stage, threshold: [0.5, 0.75] }
      );

      this.slides.forEach((slide) => this.slideObserver.observe(slide));
    }

    bindExternalVideo() {
      this.querySelectorAll('[data-external-video]').forEach((wrap) => {
        const button = wrap.querySelector('[data-external-play]');
        const template = wrap.querySelector('[data-external-embed]');
        if (!button || !template) return;

        // Facade pattern: the third-party player is only ever fetched once the
        // visitor actually asks to watch, not on page load.
        button.addEventListener('click', () => {
          wrap.innerHTML = '';
          wrap.appendChild(template.content.cloneNode(true));
          wrap.querySelector('iframe')?.focus();
        }, { once: true });
      });
    }

    goTo(index) {
      const clamped = Math.max(0, Math.min(this.slides.length - 1, index));
      this.setActive(clamped, { scroll: true });
    }

    setActive(index, { scroll }) {
      if (index < 0 || index >= this.slides.length) return;
      this.index = index;

      const slide = this.slides[index];

      if (scroll) {
        if (this.isScroller) {
          const left =
            slide.getBoundingClientRect().left -
            this.stage.getBoundingClientRect().left +
            this.stage.scrollLeft;
          this.stage.scrollTo({ left, behavior: scrollBehavior() });
        } else {
          slide.scrollIntoView({ behavior: scrollBehavior(), block: 'center' });
        }
      }

      this.thumbs.forEach((thumb, i) => {
        const isActive = i === index;
        thumb.setAttribute('aria-current', String(isActive));
        if (isActive && this.thumbRail) this.scrollThumbIntoView(thumb);
      });

      if (this.counter) this.counter.textContent = index + 1;

      // Pause any video that just left the stage.
      this.slides.forEach((s, i) => {
        if (i === index) return;
        s.querySelector('video')?.pause();
      });

      this.syncArrows();
    }

    scrollThumbIntoView(thumb) {
      const rail = this.thumbRail;
      const railBox = rail.getBoundingClientRect();
      const thumbBox = thumb.getBoundingClientRect();
      if (thumbBox.left >= railBox.left && thumbBox.right <= railBox.right) return;

      rail.scrollTo({
        left: thumb.offsetLeft - rail.clientWidth / 2 + thumb.clientWidth / 2,
        behavior: scrollBehavior(),
      });
    }

    syncArrows() {
      const multiple = this.slides.length > 1;
      if (this.prev) {
        this.prev.hidden = !multiple;
        this.prev.disabled = this.index === 0;
      }
      if (this.next) {
        this.next.hidden = !multiple;
        this.next.disabled = this.index === this.slides.length - 1;
      }
    }

    /** Called by <variant-selects> when the chosen variant has its own image. */
    selectMedia(mediaId) {
      if (!mediaId) return;
      const index = this.slides.findIndex((s) => Number(s.dataset.mediaId) === Number(mediaId));
      if (index >= 0 && index !== this.index) this.goTo(index);
    }
  }

  /* ========================================================================
     2. Variant selection
     ======================================================================== */
  class VariantSelects extends HTMLElement {
    connectedCallback() {
      const json = this.querySelector('[data-variant-data]');
      if (!json) return;

      try {
        this.variants = JSON.parse(json.textContent);
      } catch {
        return;
      }

      this.section = this.closest('[data-product-section]');
      this.sectionId = this.dataset.sectionId;
      this.productUrl = this.dataset.productUrl;
      this.shouldUpdateUrl = this.dataset.updateUrl !== 'false';
      this.abort = null;

      this.addEventListener('change', () => this.onChange());
      this.refreshReachability();
    }

    disconnectedCallback() {
      this.abort?.abort();
    }

    get fieldsets() {
      return Array.from(this.querySelectorAll('fieldset[data-option-position]'));
    }

    /** Currently chosen value per option, indexed by option position - 1. */
    selectedValues() {
      const values = [];
      this.fieldsets.forEach((fs) => {
        const index = Number(fs.dataset.optionPosition) - 1;
        const select = fs.querySelector('[data-option-select]');
        const checked = fs.querySelector('[data-option-value]:checked');
        values[index] = select ? select.value : checked ? checked.value : null;
      });
      return values;
    }

    match(values) {
      return this.variants.find(
        (v) => v.options.length === values.length && v.options.every((o, i) => o === values[i])
      );
    }

    onChange() {
      const values = this.selectedValues();
      const variant = this.match(values);

      this.refreshReachability();
      this.updateLegends(values);

      if (!variant) {
        this.setUnavailable();
        return;
      }

      // Instant, local: media and URL. No waiting on the network for these.
      this.section?.querySelector('product-gallery')?.selectMedia(variant.media);
      this.updateUrl(variant.id);
      this.setVariantInput(variant.id);

      // Authoritative, remote: price, stock, SKU, buy buttons.
      this.refreshSection(variant.id);
    }

    /**
     * Marks each option value as reachable or not, given everything else
     * currently selected. Values stay clickable on purpose — greying out a
     * size without letting the visitor click it hides why it is unavailable.
     */
    refreshReachability() {
      const selected = this.selectedValues();

      this.fieldsets.forEach((fs) => {
        const index = Number(fs.dataset.optionPosition) - 1;

        const test = (value) => {
          const candidate = selected.slice();
          candidate[index] = value;
          const found = this.match(candidate);
          return Boolean(found && found.available);
        };

        fs.querySelectorAll('[data-option-value]').forEach((input) => {
          input.toggleAttribute('data-unavailable', !test(input.value));
        });

        fs.querySelectorAll('[data-option-select] option').forEach((option) => {
          option.toggleAttribute('data-unavailable', !test(option.value));
        });
      });
    }

    updateLegends(values) {
      this.fieldsets.forEach((fs) => {
        const index = Number(fs.dataset.optionPosition) - 1;
        const node = fs.querySelector('[data-selected-value]');
        if (node) node.textContent = values[index] || '';
      });
    }

    setVariantInput(id) {
      const input = this.section?.querySelector('[data-variant-input]');
      if (input) input.value = id;
    }

    updateUrl(id) {
      if (!this.shouldUpdateUrl) return;
      const url = new URL(window.location.href);
      url.searchParams.set('variant', id);
      // replaceState, not pushState: a variant switch is not a new page in the
      // visitor's history, and pushing would trap them behind Back presses.
      window.history.replaceState({}, '', url);
    }

    setUnavailable() {
      const button = this.section?.querySelector('[data-add-button]');
      const label = this.section?.querySelector('[data-add-label]');
      if (button) button.disabled = true;
      if (label) label.textContent = strings.unavailable || 'Unavailable';
    }

    setBusy(busy) {
      const button = this.section?.querySelector('[data-add-button]');
      if (button) button.toggleAttribute('data-loading', busy);
      this.section?.setAttribute('aria-busy', String(busy));
    }

    async refreshSection(variantId) {
      // Rapid clicking through swatches: only the newest answer may land.
      this.abort?.abort();
      this.abort = new AbortController();
      this.setBusy(true);

      const url = `${this.productUrl}?variant=${variantId}&section_id=${this.sectionId}`;

      try {
        const response = await fetch(url, { signal: this.abort.signal });
        if (!response.ok) throw new Error(response.statusText);

        const doc = new DOMParser().parseFromString(await response.text(), 'text/html');

        [
          '[data-price-container]',
          '[data-sku-container]',
          '[data-inventory-container]',
          '[data-buy-container]',
        ].forEach((selector) => {
          const fresh = doc.querySelector(selector);
          const current = this.section?.querySelector(selector);
          if (fresh && current) current.innerHTML = fresh.innerHTML;
        });

        // The accelerated wallet buttons were just replaced, so Shopify's
        // script has to re-attach to the new nodes or they render dead.
        if (window.Shopify?.PaymentButton) window.Shopify.PaymentButton.init();
      } catch (error) {
        if (error.name !== 'AbortError') {
          // Leave the last known-good price on screen rather than blanking it.
          console.warn('[Xelphora] variant refresh failed:', error.message);
        }
      } finally {
        if (!this.abort?.signal.aborted) this.setBusy(false);
      }
    }
  }

  /* ========================================================================
     3. Add to cart
     ======================================================================== */
  class ProductForm extends HTMLElement {
    connectedCallback() {
      this.form = this.querySelector('form');
      if (!this.form) return;

      this.button = this.querySelector('[data-add-button]');
      this.label = this.querySelector('[data-add-label]');
      this.errorNode = this.querySelector('[data-form-error]');

      this.form.addEventListener('submit', (event) => this.onSubmit(event));
    }

    async onSubmit(event) {
      // With a cart page rather than a drawer, let the browser post normally —
      // there is nowhere to show an Ajax result.
      if (config.cartType !== 'drawer' || !Theme.cart) return;

      event.preventDefault();
      if (this.button?.disabled) return;

      const data = new FormData(this.form);
      const payload = {
        id: Number(data.get('id')),
        quantity: Number(data.get('quantity') || 1),
      };

      // Carry line item properties and subscription plans that app blocks or
      // custom inputs may have added to the form.
      const properties = {};
      data.forEach((value, key) => {
        const match = key.match(/^properties\[(.+)\]$/);
        if (match && value !== '') properties[match[1]] = value;
      });
      if (Object.keys(properties).length) payload.properties = properties;

      const plan = data.get('selling_plan');
      if (plan) payload.selling_plan = Number(plan);

      this.setError('');
      this.setState('loading');

      try {
        const cart = await Theme.cart.add(payload);
        this.setState('added');
        Theme.cart.publish(cart, { sections: cart.sections, open: true });
        setTimeout(() => this.setState('idle'), 1600);
      } catch (error) {
        this.setState('idle');
        this.setError(error.message || strings.cartError);
      }
    }

    setState(state) {
      if (!this.button) return;
      this.button.toggleAttribute('data-loading', state === 'loading');
      this.button.disabled = state === 'loading';

      if (!this.label) return;
      if (state === 'loading') this.label.textContent = strings.adding || 'Adding';
      else if (state === 'added') this.label.textContent = strings.added || 'Added';
      else this.label.textContent = strings.addToCart || 'Add to cart';
    }

    setError(message) {
      if (!this.errorNode) return;
      this.errorNode.textContent = message;
      this.errorNode.toggleAttribute('hidden', !message);
    }
  }

  /* ========================================================================
     4. Quantity stepper
     Scoped to [data-quantity]; the cart drawer's stepper uses
     [data-quantity-wrapper] and is owned by <cart-items> in global.js.
     ======================================================================== */
  const Quantity = {
    init() {
      document.addEventListener('click', (event) => {
        const step = event.target.closest('[data-quantity] [data-quantity-step]');
        if (!step) return;
        event.preventDefault();

        const wrap = step.closest('[data-quantity]');
        const input = wrap.querySelector('[data-quantity-input]');
        if (!input) return;

        const min = Number(input.min) || 1;
        const max = input.max ? Number(input.max) : Infinity;
        const next = Math.min(max, Math.max(min, Number(input.value) + Number(step.dataset.quantityStep)));

        if (next === Number(input.value)) return;
        input.value = next;
        input.dispatchEvent(new Event('change', { bubbles: true }));
      });
    },
  };

  /* ========================================================================
     5. Share
     ======================================================================== */
  class ShareButton extends HTMLElement {
    connectedCallback() {
      this.trigger = this.querySelector('[data-share-trigger]');
      this.feedback = this.querySelector('[data-share-feedback]');
      this.url = this.dataset.shareUrl || window.location.href;

      this.trigger?.addEventListener('click', () => this.share());
    }

    async share() {
      // Native sheet where it exists (essentially all mobile), clipboard
      // everywhere else. Never a row of hard-coded social links.
      if (navigator.share) {
        try {
          await navigator.share({ title: document.title, url: this.url });
          return;
        } catch {
          // Visitor dismissed the sheet — not an error.
          return;
        }
      }

      try {
        await navigator.clipboard.writeText(this.url);
        this.say(strings.shareCopied || 'Link copied');
      } catch {
        this.say(this.url);
      }
    }

    say(message) {
      if (!this.feedback) return;
      this.feedback.textContent = message;
      clearTimeout(this._timer);
      this._timer = setTimeout(() => (this.feedback.textContent = ''), 2500);
    }
  }

  /* ========================================================================
     6. Lazy recommendations
     ======================================================================== */
  class ProductRecommendations extends HTMLElement {
    connectedCallback() {
      if (!routes.productRecommendations || !this.dataset.productId) return;
      // Already server-rendered (this is the fetched copy) — nothing to do.
      if (this.querySelector('[data-carousel-track]')) return;

      if (!('IntersectionObserver' in window)) {
        this.load();
        return;
      }

      this.observer = new IntersectionObserver(
        (entries) => {
          if (!entries[0].isIntersecting) return;
          this.observer.disconnect();
          this.load();
        },
        // Start fetching well before it comes into view so the carousel is
        // already painted by the time the visitor reaches it.
        { rootMargin: '0px 0px 500px 0px' }
      );
      this.observer.observe(this);
    }

    disconnectedCallback() {
      this.observer?.disconnect();
    }

    async load() {
      const params = new URLSearchParams({
        section_id: this.dataset.sectionId,
        product_id: this.dataset.productId,
        limit: this.dataset.limit || '6',
        intent: this.dataset.intent || 'related',
      });

      try {
        const response = await fetch(`${routes.productRecommendations}?${params}`);
        if (!response.ok) throw new Error(response.statusText);

        const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
        const fresh = doc.querySelector('product-recommendations');

        // No recommendations configured for this product: remove the whole
        // section rather than leaving an empty heading behind.
        if (!fresh || !fresh.innerHTML.trim()) {
          this.remove();
          return;
        }

        this.innerHTML = fresh.innerHTML;
        Theme.reveal?.scan(this);
      } catch {
        this.remove();
      }
    }
  }

  /* ========================================================================
     7. Boot
     ======================================================================== */
  const define = (name, ctor) => {
    if (!customElements.get(name)) customElements.define(name, ctor);
  };

  define('product-gallery', ProductGallery);
  define('variant-selects', VariantSelects);
  define('product-form', ProductForm);
  define('share-button', ShareButton);
  define('product-recommendations', ProductRecommendations);

  Quantity.init();

  /* Keep the "already in your cart" line truthful after an Ajax add. The
     count comes straight from the cart payload, so no extra request and no
     money formatting — just a number. */
  document.addEventListener('cart:updated', (event) => {
    const items = event.detail?.cart?.items;
    if (!Array.isArray(items)) return;

    document.querySelectorAll('[data-product-section]').forEach((section) => {
      const input = section.querySelector('[data-variant-input]');
      const line = section.querySelector('[data-in-cart]');
      const count = section.querySelector('[data-in-cart-count]');
      if (!input || !line || !count) return;

      const id = Number(input.value);
      const entry = items.find((item) => item.variant_id === id);
      const quantity = entry ? entry.quantity : 0;

      count.textContent = quantity;
      line.toggleAttribute('hidden', quantity === 0);
    });
  });
})();
